import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Api from 'services/api';
import tabId from 'services/tabId';
import { formatDateTimeLong } from 'services/ai_management_dates';
import { ApplicationState } from 'store';
import { BiddingData, CommentDataTypes } from 'store/modules/bidding_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import CommentInput from 'components/molecules/comment_input';
import Loader from 'components/atoms/loader';
import { Container } from './styles';

interface AiManagementBiddingNotesProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
  patch: (updater: (current: BiddingData) => BiddingData) => void;
}

const AiManagementBiddingNotes: React.FC<AiManagementBiddingNotesProps> = ({
  bidding,
  loading,
  error,
  patch,
}) => {
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [submitting, setSubmitting] = useState(false);
  const [newIds, setNewIds] = useState<number[]>([]);

  const comments = bidding?.client_comments ?? [];

  const handleCreate = (message: string) => {
    if (!bidding || !message.trim()) return;

    const isRepresentant =
      profile.representant && profile.currentCompany[tabId()] !== profile.clientId;
    const data = {
      bidding_comment: {
        bidding_id: Number(bidding.bidding_id) || Number(bidding.id),
        message,
        ...(isRepresentant ? { client_id: profile.currentCompany[tabId()] } : {}),
      },
    };

    setSubmitting(true);
    Api.post('/minhas_licitacoes/comments', data)
      .then((response) => {
        if (response?.status >= 400 || !response?.data?.id) return;
        const newComment: CommentDataTypes = {
          id: response.data.id,
          message: response.data.message,
          created_at: response.data.created_at,
          contact_client: { id: profile.id, nome: profile.name },
        };
        patch((current) => ({
          ...current,
          client_comments: [...(current.client_comments ?? []), newComment],
        }));
        setNewIds((prev) => [...prev, newComment.id]);
      })
      .catch(() => {})
      .finally(() => setSubmitting(false));
  };

  const handleDelete = (id: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Excluir esta anotação?')) return;
    Api.delete(`/minhas_licitacoes/comments/${id}`)
      .then((response) => {
        if (response?.status >= 400) return;
        patch((current) => ({
          ...current,
          client_comments: (current.client_comments ?? []).filter(
            (comment) => Number(comment.id) !== Number(id),
          ),
        }));
      })
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex-grow-1 d-flex align-items-center justify-content-center">
        <Loader />
      </div>
    );
  }

  if (error || !bidding) {
    return (
      <div className="alert alert-warning m-4" role="alert">
        Não foi possível carregar as anotações.
      </div>
    );
  }

  return (
    <Container>
      <div className="notes-header px-4 pt-4 pb-2">
        <h4 className="mb-0">Anotações</h4>
      </div>

      <div className="notes-scroll px-4">
        {comments.length === 0 && <div className="text-muted mb-3">Nenhuma anotação ainda.</div>}

        {comments.map((comment) => (
          <div key={comment.id} className="note-card d-flex justify-content-between p-3 mb-3">
            <div className="flex-grow-1 mr-3">
              <div className="d-flex align-items-center mb-1">
                <span className="note-author font-weight-bold">{comment.contact_client?.nome}</span>
                {newIds.includes(comment.id) && (
                  <span className="badge badge-danger ml-2">Novo</span>
                )}
              </div>
              <div className="note-message">{comment.message}</div>
            </div>
            <div className="text-right text-nowrap">
              <span className="text-secondary">{formatDateTimeLong(comment.created_at)}</span>
              {profile.id === comment.contact_client?.id && (
                <button
                  type="button"
                  className="note-delete btn btn-link p-0 ml-2"
                  onClick={() => handleDelete(comment.id)}
                >
                  Excluir
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="notes-footer px-4 pt-2 pb-4">
        <CommentInput
          isLoading={submitting}
          createComment={handleCreate}
          commentSource="biddings"
        />
      </div>
    </Container>
  );
};

export default AiManagementBiddingNotes;
